import React, { useState, useEffect, useRef, useCallback } from 'react';

const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
  wood:         '#c9b79c',
  woodDark:     '#a8916f',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

const MIN_DISKS = 3;
const MAX_DISKS = 8;
const DISK_H    = 26;   // px height of each disk
const DISK_GAP  = 3;    // px gap between stacked disks
const PEG_LABELS = ['A', 'B', 'C'];
const PEG_NAMES  = ['Source', 'Auxiliary', 'Target'];

// Disk colour: gradient across the palette from teal-green (small) to indigo (large)
function diskColor(size, total) {
  const t = total <= 1 ? 0 : (size - 1) / (total - 1);
  const hue = 150 + t * 78;
  return { fill: `hsl(${hue.toFixed(0)}, 42%, 58%)`, edge: `hsl(${hue.toFixed(0)}, 42%, 42%)` };
}

// Percentage width (of a peg column) for a disk of the given size
function diskPct(size, total) {
  const minW = 34, maxW = 92;
  if (total <= 1) return maxW;
  return minW + ((size - 1) / (total - 1)) * (maxW - minW);
}

const buildTower = (n) => [Array.from({ length: n }, (_, i) => n - i), [], []];

// ── Recursion diagram (small illustrative 4-disk boards) ───────────────────────
// A single mini board showing a 3-peg configuration + one curved "move" arrow.
function MiniBoard({ state, arrow, aid }) {
  const pegX = { A: 40, B: 100, C: 160 };
  const baseY = 100;
  const rodTop = 34;
  const dH = 11, dGap = 1.5;
  const total = 4;
  const widthFor = (size) => 14 + (size - 1) * 12; // size 1..4 → 14..50

  const disks = [];
  Object.entries(state).forEach(([peg, list]) => {
    list.forEach((size, idx) => {
      const w = widthFor(size);
      const y = baseY - (idx + 1) * (dH + dGap);
      const { fill, edge } = diskColor(size, total);
      const moving = arrow && arrow.pegHas && arrow.from === peg && arrow.disks.includes(size);
      disks.push(
        <g key={`${peg}-${size}`}>
          <rect
            x={pegX[peg] - w / 2} y={y} width={w} height={dH} rx={3}
            fill={fill} stroke={moving ? B.amberDark : edge}
            strokeWidth={moving ? 2 : 1}
          />
        </g>
      );
    });
  });

  // Curved arrow from source peg to destination peg, arcing over the top
  let arrowEl = null;
  if (arrow) {
    const sx = pegX[arrow.from], ex = pegX[arrow.to];
    const sy = 26, ey = 26;
    const cx = (sx + ex) / 2, cy = 4;
    arrowEl = (
      <path
        d={`M ${sx} ${sy} Q ${cx} ${cy} ${ex} ${ey}`}
        fill="none" stroke={B.amberDark} strokeWidth={2}
        markerEnd={`url(#ah-${aid})`}
      />
    );
  }

  return (
    <svg viewBox="0 0 200 112" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <marker id={`ah-${aid}`} markerWidth="7" markerHeight="7" refX="5" refY="3"
          orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L6,3 L0,6 Z" fill={B.amberDark} />
        </marker>
      </defs>
      {/* pegs */}
      {Object.values(pegX).map((x, i) => (
        <rect key={i} x={x - 2.5} y={rodTop} width={5} height={baseY - rodTop} rx={2.5} fill={B.wood} />
      ))}
      {/* base */}
      <rect x={10} y={baseY} width={180} height={6} rx={3} fill={B.woodDark} />
      {disks}
      {arrowEl}
      {/* peg labels */}
      {Object.entries(pegX).map(([label, x]) => (
        <text key={label} x={x} y={111} textAnchor="middle"
          fontFamily={font} fontSize="8" fontWeight="700" fill={B.faint}>{label}</text>
      ))}
    </svg>
  );
}

function RecursionDiagram() {
  // n = 4 example. The top three disks [3,2,1] are "the n−1 smaller disks".
  const steps = [
    {
      caption: 'Move the top n−1 disks from A → B',
      sub: 'Solve the same puzzle for one fewer disk (recursion)',
      state: { A: [4, 3, 2, 1], B: [], C: [] },
      arrow: { from: 'A', to: 'B', disks: [3, 2, 1], pegHas: true },
    },
    {
      caption: 'Move disk n from A → C',
      sub: 'The single base move: the largest disk goes home',
      state: { A: [4], B: [3, 2, 1], C: [] },
      arrow: { from: 'A', to: 'C', disks: [4], pegHas: true },
    },
    {
      caption: 'Move the n−1 disks from B → C',
      sub: 'Solve the smaller puzzle again, onto the target',
      state: { A: [], B: [3, 2, 1], C: [4] },
      arrow: { from: 'B', to: 'C', disks: [3, 2, 1], pegHas: true },
    },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
      {steps.map((st, i) => (
        <div key={i} style={{
          flex: '1 1 180px', minWidth: 170,
          border: `1px solid ${B.border}`, borderRadius: 10, padding: '0.7rem 0.7rem 0.5rem',
          background: B.bg,
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6,
          }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', background: B.green, color: '#fff',
              fontSize: '0.72rem', fontWeight: 800, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{i + 1}</span>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: B.text }}>{st.caption}</span>
          </div>
          <MiniBoard state={st.state} arrow={st.arrow} aid={i} />
          <div style={{ fontSize: '0.72rem', color: B.faint, lineHeight: 1.45, marginTop: 4 }}>
            {st.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function TowersOfHanoiSimulator({ onBack }) {
  const boardRef = useRef(null);
  const pegsRef  = useRef(buildTower(MIN_DISKS));

  const [numDisks, setNumDisks] = useState(MIN_DISKS);
  const [pegs,     setPegs]     = useState(() => buildTower(MIN_DISKS));
  const [moves,    setMoves]    = useState(0);
  const [won,      setWon]      = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  // Drag state: dragInfo is stable for the duration of one drag; dragPos updates per move
  const [dragInfo, setDragInfo] = useState(null); // { size, from, w, h }
  const [dragPos,  setDragPos]  = useState({ x: 0, y: 0 });

  const minMoves = Math.pow(2, numDisks) - 1;

  const resetBoard = useCallback((n) => {
    const fresh = buildTower(n);
    pegsRef.current = fresh;
    setPegs(fresh);
    setMoves(0);
    setWon(false);
    setDragInfo(null);
  }, []);

  // Rebuild whenever disk count changes
  useEffect(() => { resetBoard(numDisks); }, [numDisks, resetBoard]);

  // Which peg column is the given clientX over? null if outside the board.
  const pegAtX = useCallback((x) => {
    const el = boardRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (x < r.left || x > r.right) return null;
    const idx = Math.floor((x - r.left) / (r.width / 3));
    return Math.max(0, Math.min(2, idx));
  }, []);

  const attemptMove = useCallback((from, to) => {
    if (to == null || to === from) return;
    const prev = pegsRef.current;
    const src = prev[from];
    if (!src.length) return;
    const disk = src[src.length - 1];
    const dst = prev[to];
    if (dst.length && dst[dst.length - 1] < disk) return; // illegal: larger onto smaller
    const next = prev.map(p => p.slice());
    next[from] = next[from].slice(0, -1);
    next[to]   = [...next[to], disk];
    pegsRef.current = next;
    setPegs(next);
    setMoves(m => m + 1);
    if (to === 2 && next[2].length === numDisks) setWon(true);
  }, [numDisks]);

  const onDiskDown = useCallback((e, pegIdx, diskIdx, size) => {
    if (won) return;
    if (diskIdx !== pegs[pegIdx].length - 1) return; // only the top disk is grabbable
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragInfo({ size, from: pegIdx, w: rect.width, h: rect.height });
    setDragPos({ x: e.clientX, y: e.clientY });
  }, [won, pegs]);

  // Global pointer listeners for the lifetime of a single drag
  useEffect(() => {
    if (!dragInfo) return;
    const move = (e) => { e.preventDefault(); setDragPos({ x: e.clientX, y: e.clientY }); };
    const up   = (e) => { attemptMove(dragInfo.from, pegAtX(e.clientX)); setDragInfo(null); };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [dragInfo, attemptMove, pegAtX]);

  const boardHeight = 44 + numDisks * (DISK_H + DISK_GAP) + 40;

  // ── Styles (shared vocabulary with the other simulators) ─────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '0 1.5rem', height: 60, gap: '1rem',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    hlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: '#efe7fb', color: '#7a5bbf', padding: '3px 7px', borderRadius: 6,
    },
    body: { maxWidth: 760, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    controlRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' },
    ctrlGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    ctrlLabel: {
      fontSize: '0.72rem', fontWeight: 700, color: B.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    select: {
      fontFamily: font, fontSize: '0.88rem', border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '6px 10px', background: B.bg, color: B.text, cursor: 'pointer',
    },
    btnGhost: {
      background: 'none', color: B.charcoal, border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '7px 16px', fontSize: '0.88rem', fontWeight: 600,
      cursor: 'pointer', fontFamily: font,
    },
    stat: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' },
    statNum: { fontSize: '1.35rem', fontWeight: 800, color: B.text, fontFamily: mono, lineHeight: 1 },
    statLabel: { fontSize: '0.66rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.04em' },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
      fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14,
    },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
    code: {
      fontFamily: mono, fontSize: '0.78rem', lineHeight: 1.7, color: B.text,
      background: B.bg, border: `1px solid ${B.border}`, borderRadius: 8,
      padding: '0.85rem 1rem', margin: '0.9rem 0 0', whiteSpace: 'pre', overflowX: 'auto',
    },
  };

  const complete = won;

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Towers of Hanoi</h1>
        <span style={s.badge}>B2.4.4</span>
        <span style={s.hlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Controls ───────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Configuration</div>
          <div style={s.controlRow}>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="disk-count">Number of disks</label>
              <select
                id="disk-count"
                style={s.select}
                value={numDisks}
                onChange={e => setNumDisks(+e.target.value)}
              >
                {Array.from({ length: MAX_DISKS - MIN_DISKS + 1 }, (_, i) => MIN_DISKS + i).map(n => (
                  <option key={n} value={n}>{n} disks</option>
                ))}
              </select>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1.4rem', alignItems: 'center' }}>
              <div style={s.stat}>
                <span style={s.statNum}>{moves}</span>
                <span style={s.statLabel}>Your moves</span>
              </div>
              <div style={{ width: 1, height: 34, background: B.border }} />
              <div style={s.stat}>
                <span style={{ ...s.statNum, color: B.greenDark }}>{minMoves}</span>
                <span style={s.statLabel}>Minimum</span>
              </div>
              <button style={s.btnGhost} onClick={() => resetBoard(numDisks)}>Restart</button>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.85rem', lineHeight: 1.55 }}>
            <strong>Goal:</strong> move the whole tower from peg <strong>A</strong> to peg <strong>C</strong>.
            Drag one disk at a time, and never place a larger disk on top of a smaller one.
            The fewest moves possible is <strong>2<sup>n</sup> − 1 = {minMoves}</strong>.
          </p>
        </div>

        {/* ── Board ──────────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Puzzle</div>
          <div
            ref={boardRef}
            style={{
              position: 'relative', display: 'flex', height: boardHeight,
              userSelect: 'none', touchAction: 'none',
            }}
          >
            {pegs.map((peg, pegIdx) => (
              <div
                key={pegIdx}
                style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                {/* rod */}
                <div style={{
                  position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)',
                  width: 8, height: boardHeight - 44, background: B.wood, borderRadius: 4, zIndex: 0,
                }} />
                {/* stacked disks (bottom-first via column-reverse) */}
                <div style={{
                  position: 'absolute', bottom: 34, left: 0, right: 0,
                  display: 'flex', flexDirection: 'column-reverse', alignItems: 'center', gap: DISK_GAP, zIndex: 1,
                }}>
                  {peg.map((size, diskIdx) => {
                    const isTop = diskIdx === peg.length - 1;
                    const hidden = dragInfo && dragInfo.from === pegIdx && isTop;
                    if (hidden) return null;
                    const { fill, edge } = diskColor(size, numDisks);
                    return (
                      <div
                        key={size}
                        onPointerDown={(e) => onDiskDown(e, pegIdx, diskIdx, size)}
                        style={{
                          width: `${diskPct(size, numDisks)}%`, height: DISK_H,
                          background: fill, border: `1.5px solid ${edge}`, borderRadius: 7,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontFamily: mono, fontSize: '0.7rem', fontWeight: 700,
                          boxShadow: '0 1px 2px rgba(60,45,30,0.12)',
                          cursor: isTop && !won ? 'grab' : 'default',
                          touchAction: 'none',
                        }}
                      >
                        {size}
                      </div>
                    );
                  })}
                </div>
                {/* base + peg label */}
                <div style={{
                  position: 'absolute', bottom: 26, left: '8%', right: '8%', height: 8,
                  background: B.woodDark, borderRadius: 4, zIndex: 0,
                }} />
                <div style={{ position: 'absolute', bottom: 4, fontSize: '0.72rem', fontWeight: 700, color: B.faint }}>
                  {PEG_LABELS[pegIdx]} · {PEG_NAMES[pegIdx]}
                </div>
              </div>
            ))}
          </div>

          {/* Result banner */}
          {complete && (() => {
            const optimal = moves === minMoves;
            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.9rem',
                padding: '10px 14px', borderRadius: 8,
                background: optimal ? B.greenSoft : '#fef9ee',
                borderLeft: `3px solid ${optimal ? B.green : B.amberDark}`,
                fontSize: '0.85rem', color: B.charcoal, lineHeight: 1.5,
              }}>
                <span style={{ fontSize: '1.1rem' }}>{optimal ? '🏆' : '✅'}</span>
                <span>
                  {optimal ? (
                    <><strong>Optimal!</strong> You solved {numDisks} disks in {moves} moves, the
                    theoretical minimum of 2<sup>{numDisks}</sup> − 1 = {minMoves}.</>
                  ) : (
                    <><strong>Solved in {moves} moves.</strong> The minimum possible is {minMoves} (2<sup>{numDisks}</sup> − 1),
                    so you used {moves - minMoves} extra. Press Restart to chase the optimal solution.</>
                  )}
                </span>
              </div>
            );
          })()}
        </div>

        {/* ── Recursion hint accordion ────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen(o => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              Hint: how recursion cracks this puzzle
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                Moving a whole tower looks hard, but it contains a <strong>smaller copy of itself</strong>.
                To move <strong>n</strong> disks from A to C, pretend you already know how to move
                <strong> n − 1</strong> disks. Then the whole problem is just three steps:
              </p>

              <RecursionDiagram />

              <p style={s.expNote}>
                Each &ldquo;move n − 1 disks&rdquo; box is the <strong>same problem, one disk smaller</strong>, so the
                method calls itself. It keeps shrinking until only <strong>one disk</strong> is left, the
                <strong> base case</strong>, which is moved directly. That is recursion: a problem defined in terms
                of a smaller version of itself, with a base case that stops it.
              </p>

              <pre style={s.code}>{`function hanoi(n, from, aux, to):
    if n == 1:                      # base case: stop recursing
        move disk from → to
    else:
        hanoi(n - 1, from, to, aux) # 1. move n−1 disks onto the spare peg
        move disk from → to         # 2. move the largest disk across
        hanoi(n - 1, aux, from, to) # 3. move n−1 disks onto the target`}</pre>

              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Why recursion fits</div>
                  <ul style={s.expList}>
                    <li>The whole solution is <strong>five lines</strong>; a loop-based version is far messier.</li>
                    <li>Well suited to problems that <strong>break into smaller, similar subproblems</strong>.</li>
                    <li>Same idea powers <strong>quicksort</strong>, <strong>binary-tree traversal</strong> and
                      <strong> fractal</strong> drawing.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Its limitations</div>
                  <ul style={s.expList}>
                    <li>Needs <strong>2<sup>n</sup> − 1</strong> moves, so the cost <strong>doubles</strong> per extra disk.</li>
                    <li>Every call adds a frame to the <strong>call stack</strong>, so deep recursion can
                      <strong> overflow memory</strong>.</li>
                    <li>Repeated work in some problems makes recursion <strong>slower</strong> than iteration.</li>
                  </ul>
                </div>
              </div>

              <p style={s.expNote}>
                The move count follows T(n) = 2·T(n − 1) + 1, which solves to <strong>2<sup>n</sup> − 1</strong>.
                For 8 disks that is already {Math.pow(2, 8) - 1} moves, showing how a compact recursive
                solution can still carry an <strong>exponential</strong> cost.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Floating disk that follows the pointer during a drag */}
      {dragInfo && (
        <div style={{
          position: 'fixed', left: dragPos.x, top: dragPos.y,
          width: dragInfo.w, height: dragInfo.h,
          transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 1000,
          background: diskColor(dragInfo.size, numDisks).fill,
          border: `1.5px solid ${diskColor(dragInfo.size, numDisks).edge}`, borderRadius: 7,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontFamily: mono, fontSize: '0.7rem', fontWeight: 700,
          boxShadow: '0 6px 16px rgba(60,45,30,0.28)',
        }}>
          {dragInfo.size}
        </div>
      )}
    </div>
  );
}
